/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Input, Label } from "@nordcloud/gnui";
import { FormGroup, Metadata } from "~/components";
import {
  ResourceGroupData,
  ResourceGroupField,
} from "~/views/resourceGroups/ResourceGroupCreate/components/ResourceGroupCreateWizard/constants";
import {
  FormField,
  FormData,
} from "~/views/resourceGroups/ResourceGroupCreate/components/ResourceGroupCreateWizard/formConfig";
import { ResourceGroupDetailsSchema } from "~/views/resourceGroups/ResourceGroupCreate/components/ResourceGroupCreateWizard/ResourceGroupDetails/resourceGroupDetailsSchema";

export type ResourceGroupDetails = {
  [ResourceGroupField.DETAILS]: FormData | undefined;
};

type Props = {
  initialValues?: {
    details: ResourceGroupData[ResourceGroupField.DETAILS];
  };
  onSubmit: SubmitHandler<FormData>;
  formId?: string;
};

export function CreateResourceGroupDetails({
  onSubmit,
  initialValues,
  formId,
}: Props) {
  const formMethods = useForm({
    resolver: zodResolver(ResourceGroupDetailsSchema),
    defaultValues: {
      [FormField.NAME]: initialValues?.details?.name ?? "",
      [FormField.DESCRIPTION]: initialValues?.details?.description ?? "",
      [FormField.METADATA]: initialValues?.details?.metadata ?? undefined,
    },
  });

  const { formState, register, handleSubmit } = formMethods;

  const submit: SubmitHandler<FormData> = (data) => {
    onSubmit({
      [FormField.NAME]: data.name,
      [FormField.DESCRIPTION]: data.description,
      [FormField.METADATA]: data.metadata,
    });
  };

  return (
    <div>
      <FormProvider {...formMethods}>
        <form id={formId} onSubmit={handleSubmit(submit)}>
          <FormGroup error={formState.errors[FormField.NAME]}>
            <Label name="Name" htmlFor="name" required />
            <Input
              id="name"
              {...register("name")}
              placeholder="Provide a name"
            />
          </FormGroup>
          <FormGroup error={formState.errors[FormField.DESCRIPTION]}>
            <Label name="Description" htmlFor="description" />
            <Input
              id="description"
              {...register("description")}
              placeholder="Provide a description"
            />
          </FormGroup>
          <Metadata />
        </form>
      </FormProvider>
    </div>
  );
}
